/**
 * Filtros salvos do Diagnóstico: o payload guarda o estado da tela, não os
 * parâmetros da busca. Quem traduz para o mirai é o buildParams() da aba, então
 * o mesmo registro serve com a busca direta ao mirai ou pelo proxy da kyojin.
 *
 * Sem dependências do Vue nem de `@/`: é importado direto pelos testes de
 * tests/unit (node --test).
 */

export const SAVED_FILTER_VERSION = 1;

const DATE_RANGE_KEYS = ["ultima_data_emissao"];

function toIsoDate(value) {
    if (!value) return null;
    if (typeof value.format === "function") return value.format("YYYY-MM-DD");
    return String(value).slice(0, 10);
}

function clone(value) {
    return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

/**
 * Só as chaves ativas (`isActive` é o isFilterActive da aba). Num `*_range`, o
 * limite que está no min/max atual da base (`rangeDefault`) vai como null, para
 * a faixa continuar aberta quando a base crescer: [1, 100] com máximo 100 é
 * salvo como [1, null].
 */
export function serializeDiagnosisState({
    filters = {},
    isActive,
    rangeDefault = () => [],
    selectedSearch,
    selectedCurve,
    tipoFornecedor,
    filtrosRapidos = [],
    sorters = [],
    mercadologicoLabels = {},
    columns,
    columnPreset,
}) {
    const saved = {};

    Object.entries(filters).forEach(([key, value]) => {
        if (!isActive(key, value)) return;

        if (DATE_RANGE_KEYS.includes(key)) {
            saved[key] = value.map(toIsoDate);
        } else if (key.endsWith("_range") && Array.isArray(value)) {
            const defaults = rangeDefault(key) || [];

            saved[key] = value.map((bound, index) =>
                defaults[index] !== undefined && bound === defaults[index]
                    ? null
                    : bound
            );
        } else {
            saved[key] = clone(value);
        }
    });

    const labels = {};

    (saved.mercadologico__in || []).forEach((value) => {
        if (mercadologicoLabels[value]) labels[value] = mercadologicoLabels[value];
    });

    return {
        version: SAVED_FILTER_VERSION,
        filters: saved,
        selectedSearch: selectedSearch ?? null,
        selectedCurve: selectedCurve ?? null,
        tipoFornecedor: tipoFornecedor ?? null,
        filtrosRapidos: [...filtrosRapidos],
        sorters: clone(sorters),
        mercadologicoLabels: labels,
        columns: Array.isArray(columns) ? [...columns] : null,
        columnPreset: columnPreset ?? null,
    };
}

/**
 * Colunas salvas que ainda existem na tela, na ordem salva. Null quando o
 * payload não tem colunas (filtros do sistema e os salvos antes delas) ou
 * nenhuma sobrou: a tela mantém as colunas atuais.
 */
export function restoreDiagnosisColumns(payload, available = []) {
    if (!Array.isArray(payload?.columns)) return null;

    const columns = payload.columns.filter((column) => available.includes(column));

    return columns.length ? [...new Set(columns)] : null;
}

/**
 * Limites dos `*_range` de um payload, para a edição manual no modal: null é
 * "sem limite"; chave ausente vira [null, null].
 */
export function rangeBoundsFromPayload(payload, keys) {
    const filters = payload?.filters || {};

    return Object.fromEntries(
        keys.map((key) => {
            const value = Array.isArray(filters[key]) ? filters[key] : [];

            return [key, [value[0] ?? null, value[1] ?? null]];
        })
    );
}

/**
 * Grava os limites editados à mão. Um número vale como está, mesmo igual ao
 * min/max atual da base; [null, null] tira o filtro do payload.
 */
export function applyRangeBounds(payload, bounds) {
    const filters = { ...(payload?.filters || {}) };

    Object.entries(bounds).forEach(([key, [min, max]]) => {
        if (min === null && max === null) {
            delete filters[key];
        } else {
            filters[key] = [min, max];
        }
    });

    return { ...payload, filters };
}

/** Texto digitado num limite: dígitos, um separador decimal e "-" no início. */
export function sanitizeBoundInput(text) {
    const raw = String(text ?? "").replace(/[^\d,.-]/g, "");
    const negative = raw.startsWith("-");
    const [integer, ...decimals] = raw.replace(/-/g, "").split(/[,.]/);
    const number = decimals.length
        ? `${integer},${decimals.join("")}`
        : integer;

    return negative ? `-${number}` : number;
}

export function parseBound(text) {
    const normalized = String(text ?? "").replace(",", ".");

    if (normalized === "" || normalized === "-" || normalized === ".") {
        return null;
    }

    const value = Number(normalized);

    return Number.isFinite(value) ? value : null;
}

export function formatBound(value) {
    return value === null || value === undefined
        ? ""
        : String(value).replace(".", ",");
}

/**
 * Mesmo remapeamento do getfilters(): o valor do nó pode mudar entre
 * sincronizações, o caminho (label) não. Sem árvore carregada, mantém os
 * valores como estão.
 */
export function remapMercadologico(values, savedLabels = {}, currentLabels = {}) {
    if (!Object.keys(currentLabels).length) return [...values];

    const valueByLabel = Object.fromEntries(
        Object.entries(currentLabels).map(([value, label]) => [label, value])
    );

    const remapped = values
        .map((value) =>
            value in currentLabels ? value : valueByLabel[savedLabels[value]]
        )
        .filter((value) => value !== undefined);

    return [...new Set(remapped)];
}

/**
 * Limite nulo num `*_range` (ex.: filtros do sistema, que não conhecem a base
 * do cliente) vira o min/max atual de `rangeDefault(key)`.
 */
export function restoreDiagnosisFilters(
    payload,
    { mercadologicoLabels = {}, toDate, rangeDefault = () => [] }
) {
    const filters = clone(payload?.filters || {});

    Object.keys(filters)
        .filter((key) => key.endsWith("_range") && Array.isArray(filters[key]))
        .forEach((key) => {
            const defaults = rangeDefault(key) || [];

            filters[key] = filters[key].map((value, index) =>
                value === null || value === undefined ? defaults[index] : value
            );
        });

    DATE_RANGE_KEYS.forEach((key) => {
        if (Array.isArray(filters[key])) {
            filters[key] = filters[key].map((value) => (value ? toDate(value) : value));
        }
    });

    if (Array.isArray(filters.mercadologico__in)) {
        filters.mercadologico__in = remapMercadologico(
            filters.mercadologico__in,
            payload.mercadologicoLabels,
            mercadologicoLabels
        );
    }

    return filters;
}
